import Cookies from 'js-cookie';

import { getAnaIdentifier } from './anaIdentifier';

/** Project name, per the analytics API convention for `origin: 'web'`. */
const PLATFORM = 'login-front';
const ORIGIN = 'web';

const EVENTS_PATH = '/api/v1/events';
const BATCH_EVENTS_PATH = '/api/v1/events/batch';

// Mirrors the event_name allowlist enforced by the analytics consumer. Names
// outside it are acked and discarded server-side, so sending them is wasted work.
const ALLOWED_EVENT_NAMES = [
  'ab_test_event',
  'add_payment_info',
  'add_shipping_info',
  'add_to_cart',
  'add_to_wishlist',
  'begin_checkout',
  'cart_page_view',
  'checkout_resume',
  'color_variant_select',
  'consent_pageview',
  'device_info',
  'lean_catalog',
  'lean_pdp_v4',
  'login',
  'page_load',
  'page_view',
  'product_page_view',
  'purchase',
  'remove_from_cart',
  'remove_from_wishlist',
  'select_content',
  'select_item',
  'select_promotion',
  'size_select',
  'view_cart',
  'view_content',
  'view_installment',
  'view_item',
  'view_item_list',
  'view_promotion',
  'view_shipping_info',
  'wishlist_add',
  'wishlist_remove',
  'page_counter_updated',
];

interface AnaExperiment {
  experiment_id: string;
  variant?: string;
}

// Same navigation experiments alice tags every event with. Variant is
// resolved from the `lean_pdp_v4` cookie set by the lean PDP experiment.
const getNavigationExperiments = (): AnaExperiment[] => {
  const variant = Cookies.get('lean_pdp_v4');

  return [
    { experiment_id: 'lean_pdp', variant },
    {
      experiment_id: 'lean_pdp_product_analytics',
      variant: variant ? `lean_pdp_product_analytics_${variant}` : variant,
    },
  ];
};

interface AnaConfig {
  apiUrl: string;
  apiKey: string;
}

let anaConfig: AnaConfig | null = null;

// Analytics must never break the login flow, so nothing in this module throws.
// Bail-outs are logged instead: a dropped event stays diagnosable from the
// console rather than just going missing from the dashboards.
const warn = (message: string) => {
  console.warn(`[ana] ${message}`);
};

export const registerAnaTransport = (apiUrl: string, apiKey: string) => {
  if (!apiUrl || !apiKey) {
    warn(
      'transport not registered: REACT_APP_ANALYTICS_PA_API_URL and/or ' +
        'REACT_APP_ANALYTICS_PA_API_KEY missing from settings.json',
    );
    return;
  }

  anaConfig = { apiUrl: apiUrl.replace(/\/+$/, ''), apiKey };
};

export interface AnaEventPayload {
  event_name: string;
  user_id?: string;
  properties?: Record<string, unknown>;
}

/**
 * Groups the navigation experiments by variant: the API carries a single
 * `variant` per event, so experiments sharing one travel together in
 * `experiment_ids` and divergent ones become separate events.
 */
const buildEvents = (sharedBody: Record<string, unknown>) => {
  const experimentIdsByVariant = new Map<string, string[]>();

  getNavigationExperiments().forEach(({ experiment_id, variant }) => {
    const grouped = experimentIdsByVariant.get(variant || '');

    if (grouped) grouped.push(experiment_id);
    else experimentIdsByVariant.set(variant || '', [experiment_id]);
  });

  return Array.from(experimentIdsByVariant).map(([variant, experiment_ids]) => {
    const event: Record<string, unknown> = { ...sharedBody, experiment_ids };
    if (variant) event.variant = variant;

    return event;
  });
};

export const trackAnaEvent = ({
  event_name,
  user_id,
  properties,
}: AnaEventPayload) => {
  if (!anaConfig) {
    warn(`"${event_name}" not sent: transport is not registered`);
    return;
  }

  if (!event_name) {
    warn('event not sent: event_name is empty');
    return;
  }

  if (!ALLOWED_EVENT_NAMES.includes(event_name)) {
    warn(`"${event_name}" not sent: not in the consumer allowlist`);
    return;
  }

  // Always the shared `ana_identifier` cookie: one session id across every web
  // platform, so callers cannot pass their own. The API validates session_id as
  // required, so an empty one would come back as a 400 instead of being ingested.
  const sessionId = getAnaIdentifier();
  if (!sessionId) {
    warn(
      `"${event_name}" not sent: no session id (ana_identifier unavailable)`,
    );
    return;
  }

  const sharedBody: Record<string, unknown> = {
    event_name,
    session_id: sessionId,
    platform: PLATFORM,
    origin: ORIGIN,
    properties: properties ?? {},
  };

  if (user_id) sharedBody.user_id = user_id;

  const { apiUrl, apiKey } = anaConfig;
  const events = buildEvents(sharedBody);

  const post = (path: string, body: unknown) => {
    fetch(`${apiUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': apiKey,
      },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) {
          warn(`"${event_name}" rejected with HTTP ${response.status}`);
        }
      })
      .catch((error) => {
        warn(`"${event_name}" failed to send: ${error}`);
      });
  };

  if (events.length === 1) {
    post(EVENTS_PATH, events[0]);
    return;
  }

  post(BATCH_EVENTS_PATH, { events });
};
